import { useEffect, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { avatarSwatchClass } from './avatarSwatch';

export function Avatar({
  avatarUrl,
  fallback,
  alt,
  imageClassName = 'size-full rounded-full object-cover',
  containerClassName,
}: {
  avatarUrl?: string | null;
  fallback: ReactNode;
  alt: string;
  imageClassName?: string;
  containerClassName?: string;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(avatarUrl) && !imageFailed;

  useEffect(() => {
    setImageFailed(false);
  }, [avatarUrl]);

  const content = showImage ? (
    <img
      src={avatarUrl ?? undefined}
      alt={alt}
      className={imageClassName}
      referrerPolicy="no-referrer"
      onError={() => setImageFailed(true)}
    />
  ) : (
    fallback
  );

  if (containerClassName) {
    return <span className={containerClassName}>{content}</span>;
  }

  return content;
}

/** Pass `seed` (a stable id such as an email) to colour the chip per person. */
export const InitialsAvatar = ({
  name,
  seed,
  className,
}: {
  name: string;
  seed?: string;
  className?: string;
}) => (
  <span
    aria-hidden
    title={name}
    className={cn(
      'size-7 shrink-0 select-none rounded-full border border-border',
      'flex items-center justify-center text-[0.625rem] font-semibold uppercase',
      seed ? avatarSwatchClass(seed) : 'bg-surface-active text-foreground',
      className
    )}
  >
    {initialsOf(name)}
  </span>
);

/** A row of overlapping avatars. Each gets a ring in the background colour, and the small overlap leaves its initials readable. */
export const AvatarStack = ({ children }: { children: ReactNode }) => (
  <span className="flex shrink-0 items-center -space-x-0.5 [&>*]:rounded-full [&>*]:ring-2 [&>*]:ring-background">
    {children}
  </span>
);

function initialsOf(name: string): string {
  const parts = name
    .trim()
    .split(/[\s@._+-]+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
